/**
 * K108-X — owner/admin "Training Needs Review" queue + resolution.
 *
 * Read: shop mismatch sessions with no owner/admin review yet (RLS "Shop members
 * can view consumption sessions" scopes to the caller's shop). Write: the
 * SECURITY DEFINER review_training_session RPC (manager/owner/admin-gated in the
 * DB). No DB change beyond the already-applied RPC.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTrainingSessionMonitoringSummary } from '@/hooks/useTrainingSessionMonitoringSummary';
import {
  buildNeedsReviewItem,
  isUnresolvedMismatch,
  type NeedsReviewItem,
  type NeedsReviewSessionInput,
  type ReviewAction,
} from '@/lib/training/trainingNeedsReview';

export function useTrainingNeedsReview(enabled: boolean) {
  const sessionsQuery = useQuery<NeedsReviewSessionInput[]>({
    queryKey: ['training-needs-review'],
    enabled,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_consumption_sessions')
        .select(
          'id, ticket_id, opened_at, updated_at, confirmed_at, metadata, artifact:training_artifacts(id, title, provider_name), ticket:tickets(id, simple_ticket_number, ticket_number), technicianProfile:technician_profiles(id, full_name)',
        )
        .eq('metadata->trainingAttestation->>mismatch', 'user_attestation_mismatch')
        .order('opened_at', { ascending: false })
        .limit(100);
      if (error) return [];
      // Only UNRESOLVED mismatches (no trainingReview yet).
      return ((data ?? []) as unknown as NeedsReviewSessionInput[]).filter(isUnresolvedMismatch);
    },
  });

  const sessions = useMemo(() => sessionsQuery.data ?? [], [sessionsQuery.data]);
  const sessionIds = useMemo(() => sessions.map((s) => s.id), [sessions]);
  const { data: summaries } = useTrainingSessionMonitoringSummary({
    sessionIds,
    enabled: enabled && sessionIds.length > 0,
  });

  const items = useMemo<NeedsReviewItem[]>(
    () => sessions.map((s) => buildNeedsReviewItem(s, summaries?.get(s.id) ?? null)),
    [sessions, summaries],
  );

  return { items, count: items.length, isLoading: sessionsQuery.isLoading };
}

export function useReviewTrainingSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      sessionId: string;
      action: ReviewAction;
      note?: string | null;
      observedPercent?: number | null;
    }) => {
      // The RPC was applied via an explicit migration; the generated types don't
      // include it yet. Call `supabase.rpc(...)` as a MEMBER expression (never
      // detach it into a const — SupabaseClient.rpc uses `this.rest`, so an unbound
      // call throws a TypeError before the request is sent). Cast only the result.
      const { error } = (await (
        supabase.rpc as unknown as (
          this: typeof supabase,
          fn: string,
          params: Record<string, unknown>,
        ) => Promise<{ error: { message: string } | null }>
      ).call(supabase, 'review_training_session', {
        _session_id: args.sessionId,
        _action: args.action,
        _note: args.note ?? null,
        _observed_percent: args.observedPercent ?? null,
      })) ?? { error: null };
      if (error) {
        // Surface the real reason during development (prod keeps a neutral toast).
        if (import.meta.env?.DEV) {
           
          console.error('[review_training_session] RPC failed:', error.message, error);
        }
        throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['training-needs-review'] });
      qc.invalidateQueries({ queryKey: ['training-session-history'] });
      qc.invalidateQueries({ queryKey: ['training-session-monitoring-summary'] });
    },
  });
}
